import { hasNativeRouteRedirection } from '../navigation/nativeRoutingRedirection';
import { getPageLinkForSlug } from '~~/helpers/routes/getPageInformation';
import type { LocalisedString } from '~~/helpers/seo/types/LocalisedString';
import type { HreflangParameters } from '~~/helpers/seo/types/HreflangParameters';
import { HREFLANG_RELATION } from '~~/constants/seo';
import { SECURE_WEBSITE_PROTOCOL_PREFIX } from '~~/constants/http';
import localizationDefinitions from '~~/i18n/localizationDefinitions';
import type CountryLocalization from '~~/types/localization/countryLocalization';
import type { languageCodes } from '~~/i18n/enums/languageCodes';

type HreflangOptions = {
  isPdp?: boolean,
  isCategoryPage?: boolean,
  isPageHreflangLinkHidden?: boolean,
}

const generateHreflangLink = (
  localisedSlugs?: LocalisedString[],
  hreflangOptions?: HreflangOptions,
): HreflangParameters[] => {

  if (hreflangOptions?.isPageHreflangLinkHidden) {

    return [];
  
  }

  if (hreflangOptions?.isPdp || (localisedSlugs && localisedSlugs.length)) {

    return getHreflangLinkForLocalisedSlugs(localisedSlugs, hreflangOptions);
  
  } else {

    return getHreflangLinkForHomePage();
  
  }

};

const getHreflangLinkForLocalisedSlugs =
(
  localisedSlugs: LocalisedString[] = [],
  hreflangOptions?: HreflangOptions,
): HreflangParameters[] => {

  const returnValues: HreflangParameters[] = [];
  let nativeRoutingIndicator: Maybe<'c' | 'p'> = null;

  if (hreflangOptions?.isPdp) {

    nativeRoutingIndicator = 'p';
  
  } else if (hreflangOptions?.isCategoryPage) {

    nativeRoutingIndicator = 'c';
  
  }
  localisedSlugs.forEach((localizedSlug: LocalisedString) => {

    const countryLocalization = localizationDefinitions.getByLanguageAndCountry(localizedSlug.locale);

    if (!countryLocalization) {

      return;
    
    }
    const prefix = countryLocalization.getLanguageFromExistingLanguageAndCountry(localizedSlug.locale);
    const hreflangInfo = generateHreflangObject({
      languageAndCountry: localizedSlug.locale,
      slug: `/${localizedSlug.value}`,
      countryLocalization,
      prefix: countryLocalization.getDefaultLanguage() !== prefix ? prefix : undefined,
      nativeRoutingIndicator,
    });

    if (hreflangInfo) {

      returnValues.push(hreflangInfo);
    
    }
  
  });

  return returnValues;

};

const getHreflangLinkForHomePage = (): HreflangParameters[] => {

  const returnValues: HreflangParameters[] = [];
  localizationDefinitions.getAllCountries().forEach((countryLocalization: CountryLocalization) => {

    countryLocalization.getLanguageAndCountries().forEach(languageAndCountry => {

      const prefix = countryLocalization.getLanguageFromExistingLanguageAndCountry(languageAndCountry);
      const hreflangInfo = generateHreflangObject({
        languageAndCountry,
        slug: '',
        countryLocalization,
        prefix: countryLocalization.getDefaultLanguage() !== prefix ? prefix : undefined,
      });

      if (hreflangInfo) {

        returnValues.push(hreflangInfo);
      
      }
    
    });
  
  });

  return returnValues;

};

const generateHreflangObject = ({
  languageAndCountry,
  slug,
  countryLocalization,
  prefix,
  nativeRoutingIndicator,
}: {
  languageAndCountry: string,
  slug: string,
  countryLocalization: CountryLocalization,
  prefix?: Maybe<ValueOf<typeof languageCodes>>,
  nativeRoutingIndicator?: Maybe<string>,
}):
  HreflangParameters => {

  const fullDomain = `${SECURE_WEBSITE_PROTOCOL_PREFIX}${countryLocalization.prodDomain}`;
  const prefixIfNeeded = prefix ? `/${prefix}` : '';
  const isNativeRouteNeeded = hasNativeRouteRedirection(countryLocalization.countryCode);
  const nativeRoutingIfNeeded = isNativeRouteNeeded && nativeRoutingIndicator ? '/' + nativeRoutingIndicator : '';
  const parameters: HreflangParameters = {
    rel: HREFLANG_RELATION,
    hreflang: languageAndCountry,
    href: `${fullDomain}${prefixIfNeeded}${nativeRoutingIfNeeded}`,
  };
  parameters.href += slug ? getPageLinkForSlug(slug) : '/';

  return parameters;

};

export default generateHreflangLink;
